<template>
  <div class="event-popup-panel">
    <img v-if="currentImageUrl" :src="currentImageUrl" class="event-popup-panel__image" />
    <div class="event-popup-panel__body">
      <div v-if="popupTypeText !== ''" class="event-popup-panel__type">{{ popupTypeText }}</div>
      <div class="event-popup-panel__title">{{ currentEvent.title }}</div>
      <EventPopupCategoryHeader :category="currentEvent.category" :online="currentEvent.has_live_schedule" />
      <div v-if="hostCompany" class="event-popup-panel__section">
        <div class="event-popup-panel__section-header">主催企業</div>
        <EventPopupCompany :company="hostCompany" />
      </div>
      <EventPopupCompaniesSection
        v-if="currentEvent.companies.length > 1"
        :companies="currentEvent.companies"
        class="event-popup-panel__section"
      />
      <div
        v-for="(item, index) in currentEvent.event_description_items"
        :key="index"
        class="event-popup-panel__section"
      >
        <div class="event-popup-panel__section-header">{{ item.label }}</div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="event-popup-panel__section-body" v-html="md.render(item.content)" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from '@vue/composition-api';
import { EventPopupItem, EventPopupItemType } from 'oc2api/popup';
import { markdownRenderer } from 'lib/markdown';
import EventPopupCategoryHeader from './EventPopupCategoryHeader.vue';
import EventPopupCompany from './EventPopupCompany.vue';
import EventPopupCompaniesSection from './EventPopupCompaniesSection.vue';
import noImageIcon from 'images/noimage.png';

const popupTypeToText: Record<EventPopupItemType, string> = {
  PR: 'PR',
  favorite: 'お気に入り企業',
  industry: '過去エントリーした業界',
  other: '',
};

export default defineComponent({
  name: 'EventPopup',
  components: {
    EventPopupCategoryHeader,
    EventPopupCompany,
    EventPopupCompaniesSection,
  },
  props: {
    item: {
      type: Object as PropType<EventPopupItem>,
      required: true,
    },
  },
  setup(props) {
    const currentImageUrl = computed(() => props.item.event.eye_catch_image_url || undefined);
    const currentEvent = computed(() => props.item.event);
    const popupTypeText = computed(() => popupTypeToText[props.item.item_type]);
    const hostCompany = computed(() =>
      currentEvent.value.host_company
        ? currentEvent.value.host_company
        : currentEvent.value.companies.length == 1
          ? currentEvent.value.companies[0]
          : undefined,
    );
    const md = markdownRenderer();

    return {
      currentEvent,
      currentImageUrl,
      popupTypeText,
      hostCompany,
      md,
    };
  },
});
</script>

<style lang="scss" scoped>
@import 'stylesheets/mixins';
@import 'stylesheets/variables';

.event-popup-panel {
  margin: 35px auto 144px;
  width: 343px;
  background: $oc-white;
  border-radius: 10px;
}

.event-popup-panel__image {
  width: 100%;
  border-radius: 10px 10px 0 0;
  height: 140px;
  object-fit: contain;
  background-color: #8e8e93;
  aspect-ratio: 1 / 1;
}

.event-popup-panel__body {
  padding: 16px;
}

.event-popup-panel__type {
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 5px 12px 4px;
  gap: 4px;
  background: $oc-primary-light60;
  border-radius: 14px;
  font-size: 10px;
  line-height: 150%;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: white;
}

.event-popup-panel__title {
  margin-top: 16px;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 140%;
  letter-spacing: 0.02em;
  color: $oc-black;
}

.event-popup-panel__category {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 8px 0 8px 17px;
  border-left: 4px solid $oc-black;
  font-weight: 300;
  font-size: 14px;
  line-height: 172%;
  letter-spacing: 0.02em;
  color: $oc-black;

  .event-popup-panel__category-icon {
    height: 15px;
    margin-right: 9px;
  }
}
.event-popup-panel__section {
  margin-top: 16px;
}

.event-popup-panel__section-header {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 27px;
  letter-spacing: 0.02em;
  color: $oc-black;
}

.event-popup-panel__section-body {
  font-weight: 300;
  font-size: 14px;
  line-height: 172%;
  letter-spacing: 0.02em;
  color: $oc-black;
}

.event-popup-panel__count {
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid $oc-white;
  font-size: 10px;
  font-weight: 600;
  color: $oc-white;
  margin-left: 12px;

  @media screen and (max-width: 768px) {
    margin-left: 0px;
  }
}
</style>
